/**
 * AutoSchedule.jsx
 * Dropping a queue of drafts into a repeating pattern.
 *
 * Built the same way as Import, and for the same reason: this is the one
 * action in the app that can commit a month of posts in a single click, so
 * nothing is written until the whole plan has been shown, in order, with the
 * date each draft lands on.
 *
 * The pattern is deliberately two shapes rather than one clever one. Days of
 * the week is how most posting is planned; dates in the month is how anything
 * tied to a cycle is planned. A single control trying to be both would be
 * worse at each.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { Calendar, Clock, Warning, Check, Plus } from '../components/Icons.jsx';
import { formatLocalInput } from '../utils/time.js';

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export default function AutoSchedule() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [data, setData] = useState(null);

  const [mode, setMode] = useState('weekly');
  const [weekdays, setWeekdays] = useState([0, 2, 4]);
  const [monthDays, setMonthDays] = useState([1, 15]);
  const [times, setTimes] = useState(['09:00', '17:30']);
  const [newTime, setNewTime] = useState('12:00');
  const [startDate, setStartDate] = useState('');
  const [chosen, setChosen] = useState([]);

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [schedule, setSchedule] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Which brand's draft list has already had its "tick everything" default
  // applied. Without this the default is re-applied on every reload.
  const seededFor = useRef(null);
  // Shown only while a bulk delete runs; one draft goes too fast to bother.
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const d = await api.autoschedule.get(brandId);
      setData(d);

      const ids = d.drafts.map((p) => p.id);
      setChosen((current) => {
        // The first look at a brand ticks everything, because deselecting is
        // easier than selecting forty things.
        if (seededFor.current !== brandId) {
          seededFor.current = brandId;
          return ids;
        }

        // Every later reload keeps the choice that was made. This reload runs
        // straight after applying, and it used to re-tick the lot — so a draft
        // that had been deliberately unticked came back ticked, and the next
        // press scheduled it. Only drafts that have gone are dropped.
        const alive = new Set(ids);
        return current.filter((id) => alive.has(id));
      });
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
    setPreview(null);
    setResult(null);
  }, [load]);

  const pattern = () => ({
    brandId,
    mode,
    weekdays,
    monthDays,
    times,
    startDate: startDate || null,
    postIds: chosen,
  });

  async function seePlan() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setPreview(await api.autoschedule.preview(pattern()));
    } catch (e) {
      setError(e);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.autoschedule.apply({ ...pattern(), schedule }));
      setPreview(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Removes one draft outright.
   *
   * Deliberately per-row rather than "delete the ticked ones": the ticks on
   * this screen mean "schedule these", and a button that reused them to delete
   * would eventually catch somebody who had just ticked forty.
   */
  async function removeDraft(draft) {
    const label = draft.preview?.trim() ? `"${draft.preview.slice(0, 60)}…"` : 'this untitled draft';
    const sure = confirm(
      `Delete ${label}?\n\nThis cannot be undone. It has not been published anywhere.`,
    );
    if (!sure) return;

    setBusy(true);
    setError(null);
    try {
      await api.posts.remove(draft.id);
      // Drop it from the selection too, or the plan would still count it.
      setChosen((current) => current.filter((id) => id !== draft.id));
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Clears the whole draft list for this brand.
   *
   * Says "all" rather than acting on the ticks, because on this screen a tick
   * means "schedule this" — a bulk delete that read them would eventually take
   * the forty somebody had just selected to go out.
   *
   * Deleted one at a time so a draft that refuses cannot take the rest with it.
   */
  async function removeAllDrafts() {
    const all = data?.drafts ?? [];
    if (all.length === 0) return;

    const sure = confirm(
      `Delete all ${all.length} draft${all.length === 1 ? '' : 's'} for this brand?\n\n` +
        'This cannot be undone. None of them have been published, so nothing is ' +
        'removed from Facebook or Instagram.',
    );
    if (!sure) return;

    setBusy(true);
    setError(null);

    let done = 0;
    const failed = [];

    for (const draft of all) {
      try {
        await api.posts.remove(draft.id);
      } catch (e) {
        failed.push(e.message);
      }
      setProgress({ done: ++done, total: all.length });
    }

    setChosen([]);
    await load();
    setProgress(null);
    setBusy(false);

    const gone = all.length - failed.length;
    if (failed.length > 0) {
      setError(
        new Error(
          `${gone} of ${all.length} deleted. ${failed.slice(0, 2).join('; ')}` +
            `${failed.length > 2 ? ` and ${failed.length - 2} more` : ''}.`,
        ),
      );
    }
  }

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value].sort((a, b) => a - b));

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Auto-schedule</div>
          <h1>Put a queue of drafts on a repeating pattern</h1>
          <p>
            Choose the days and the times, and Kreeboom spreads your drafts across them in order.
            Nothing is scheduled until you have seen exactly where each one lands.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {progress && (
        <div className="notice notice--info">
          <Check />
          <span>
            Deleting {progress.done} of {progress.total}…
          </span>
        </div>
      )}

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {result && <Result result={result} onDone={() => setResult(null)} />}

      {!data ? (
        <div className="card">
          <p className="hint">Loading…</p>
        </div>
      ) : data.drafts.length === 0 ? (
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Calendar size={21} />
            </span>
            <div className="card__head-text">
              <h2>No drafts to schedule</h2>
              <p>
                Write some on the Create screen, bring them in from a spreadsheet on Import, or ask
                Claude to draft them. They appear here automatically.
              </p>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* ---------- the pattern ---------- */}
          <section className="card">
            <div className="card__head">
              <span className="tile">
                <Clock size={21} />
              </span>
              <div className="card__head-text">
                <h2>When to post</h2>
                <p>London time, all year — a slot does not drift when the clocks change.</p>
              </div>
            </div>

            <div className="field">
              <label>Repeat by</label>
              <div className="btn-row">
                {Object.entries(data.modes).map(([key, m]) => (
                  <button
                    key={key}
                    type="button"
                    className={`btn btn--sm ${mode === key ? 'btn--primary' : ''}`}
                    onClick={() => setMode(key)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="hint">{data.modes[mode]?.hint}</p>
            </div>

            {mode === 'weekly' ? (
              <div className="field">
                <label>Days of the week</label>
                <div className="chips">
                  {data.weekdays.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`chip-btn${weekdays.includes(d.value) ? ' is-on' : ''}`}
                      onClick={() => toggle(weekdays, setWeekdays, d.value)}
                    >
                      {d.short}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="field">
                <label>Dates in the month</label>
                <div className="chips chips--dense">
                  {MONTH_DAYS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`chip-btn chip-btn--num${monthDays.includes(d) ? ' is-on' : ''}`}
                      onClick={() => toggle(monthDays, setMonthDays, d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <p className="hint">
                  A month without that date is skipped rather than shifted — the 31st never becomes
                  the 1st.
                </p>
              </div>
            )}

            <div className="field">
              <label>Times each day</label>
              <div className="chips">
                {times.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="chip-btn is-on"
                    title="Remove this time"
                    onClick={() => setTimes(times.filter((x) => x !== t))}
                  >
                    {t} ✕
                  </button>
                ))}
              </div>

              <div className="inline-form" style={{ borderTop: 'none', paddingTop: 10, marginTop: 4 }}>
                <div className="field" style={{ flex: '0 0 140px' }}>
                  <input
                    type="time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    aria-label="Another time"
                  />
                </div>
                <button
                  className="btn btn--sm"
                  disabled={!newTime || times.includes(newTime) || times.length >= data.limits.maxTimesPerDay}
                  onClick={() => setTimes([...times, newTime].sort())}
                >
                  <Plus size={14} /> Add a time
                </button>
              </div>

              <p className="hint">
                {times.length} a day
                {mode === 'weekly' && weekdays.length > 0
                  ? ` × ${weekdays.length} day${weekdays.length === 1 ? '' : 's'} a week = ${times.length * weekdays.length} posts a week`
                  : ''}
              </p>
            </div>

            <div className="field">
              <label htmlFor="as-start">Start from (optional)</label>
              <input
                id="as-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ maxWidth: 200 }}
              />
              <p className="hint">Leave blank to begin at the next slot from now.</p>
            </div>
          </section>

          {/* ---------- which drafts ---------- */}
          <section className="card">
            <div className="card__head">
              <div className="card__head-text">
                <h2>
                  {chosen.length} of {data.drafts.length} drafts
                </h2>
                <p>They go out in this order, oldest first. Untick anything to leave behind.</p>
              </div>
              <div className="card__head-end">
                <button
                  className="btn btn--sm"
                  onClick={() =>
                    setChosen(chosen.length === data.drafts.length ? [] : data.drafts.map((d) => d.id))
                  }
                >
                  {chosen.length === data.drafts.length ? 'Untick all' : 'Tick all'}
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  onClick={removeAllDrafts}
                  disabled={busy || data.drafts.length === 0}
                  title="Delete every draft for this brand"
                >
                  Delete all
                </button>
              </div>
            </div>

            <div className="rows">
              {data.drafts.map((d, i) => {
                const on = chosen.includes(d.id);
                return (
                  <div className={`row row--pick${on ? ' is-picked' : ''}`} key={d.id}>
                    {/* The label covers the tick and the wording only — a button
                        inside it would toggle the tick as well as fire. */}
                    <label
                      style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          setChosen(on ? chosen.filter((id) => id !== d.id) : [...chosen, d.id])
                        }
                      />
                      <span className="besttimes__rank">{i + 1}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="row__label">
                          {d.preview?.trim() ? d.preview.slice(0, 90) : 'Untitled draft'}
                        </div>
                        <div className="row__sub">
                          {d.platforms || 'no platforms'}
                          {d.scheduled_at_utc ? ' · already has a time' : ''}
                        </div>
                      </div>
                    </label>

                    <div className="row__end">
                      <button
                        className="btn btn--sm btn--danger"
                        onClick={() => removeDraft(d)}
                        disabled={busy}
                        title="Delete this draft"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                className="btn btn--primary"
                onClick={seePlan}
                disabled={busy || chosen.length === 0}
              >
                {busy ? 'Working it out…' : 'See the plan'}
              </button>
            </div>
          </section>

          {/* ---------- the plan ---------- */}
          {preview && (
            <Plan
              preview={preview}
              busy={busy}
              schedule={schedule}
              setSchedule={setSchedule}
              onApply={apply}
              onCancel={() => setPreview(null)}
            />
          )}
        </>
      )}
    </>
  );
}

/** Exactly where every draft lands, before anything is written. */
function Plan({ preview, busy, schedule, setSchedule, onApply, onCancel }) {
  const { assignments, skipped, summary } = preview;

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>The plan</h2>
          <p>{summary} Nothing has been scheduled yet.</p>
        </div>
      </div>

      {assignments.length === 0 ? (
        <div className="notice notice--error">
          <Warning />
          <span>
            Nothing fits this pattern. Add more days or times, or start earlier.
          </span>
        </div>
      ) : (
        <>
          <div className="importrows">
            {assignments.map((a, i) => (
              <div className="importrow" key={a.id}>
                <span className="importrow__line">{i + 1}</span>
                <div className="importrow__body">
                  <div className="importrow__top">
                    <span className="badge badge--accent">{formatLocalInput(a.whenLocal)}</span>
                    {a.platforms.map((p) => (
                      <span className="badge" key={p}>
                        {p}
                      </span>
                    ))}
                  </div>
                  <p className="importrow__caption">{a.preview || 'Untitled draft'}</p>
                </div>
              </div>
            ))}
          </div>

          {skipped.length > 0 && (
            <details className="details" open>
              <summary>{skipped.length} could not be placed</summary>
              <ul className="plainlist">
                {skipped.map((s) => (
                  <li key={s.id}>
                    <b>{(s.preview || 'Untitled draft').slice(0, 60)}</b> — {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <label className="checkline" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              checked={schedule}
              onChange={(e) => setSchedule(e.target.checked)}
            />
            <span>
              <b>Schedule them, not just pencil them in.</b>
              <span className="checkline__hint">
                {schedule
                  ? 'On. These go into the scheduler and publish at their slot without being ' +
                    'approved again. They stay visible on the Calendar, so any of them can be ' +
                    'cancelled.'
                  : 'Off. Each draft gets its time filled in but stays a draft, waiting for you to ' +
                    'approve it. Useful for laying out a month before committing to it.'}
              </span>
            </span>
          </label>

          <div className="btn-row" style={{ marginTop: 14 }}>
            <button className="btn btn--primary" onClick={onApply} disabled={busy}>
              <Check />
              {busy
                ? 'Working…'
                : schedule
                  ? `Schedule ${assignments.length}`
                  : `Give ${assignments.length} a time`}
            </button>
            <button className="btn" onClick={onCancel} disabled={busy}>
              Change the pattern
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function Result({ result, onDone }) {
  return (
    <section className="card">
      <div className="notice notice--ok">
        <Check />
        <span>{result.summary}</span>
      </div>

      {result.failed.length > 0 && (
        <details className="details" open>
          <summary>{result.failed.length} could not be scheduled</summary>
          <ul className="plainlist">
            {result.failed.map((f) => (
              <li key={f.id}>
                <b>{(f.preview || 'Untitled draft').slice(0, 60)}</b> — {f.reason}
              </li>
            ))}
          </ul>
          <p className="hint">
            These kept their time and stayed as drafts, so nothing was lost. Fix the reason and
            they are one click from scheduled.
          </p>
        </details>
      )}

      <div className="btn-row" style={{ marginTop: 14 }}>
        <a className="btn btn--primary" href="/calendar">
          Open the Calendar
        </a>
        <button className="btn" onClick={onDone}>
          Schedule more
        </button>
      </div>
    </section>
  );
}
